/**
 * PackListFilters.jsx — RC 1.47.P0.2B Slice B
 *
 * URL-driven filter bar for the Applications list. Mirrors the
 * grammar of `JobsPage`'s filter row.
 *
 *   - Pack-name search (`q`) — locally controlled, debounced into
 *     the URL after FILTER_DEBOUNCE_MS so typing doesn't spam the
 *     backend.
 *   - Status multi-chip (`status`) — toggleable; URL stores the
 *     CSV form (e.g. `?status=ready,submitted`).
 *   - Sort dropdown (`sort`) — six options matching backend enum.
 *   - Clear filters — wipes every filter param + resets to page 1.
 *   - "Showing X of Y packs" counter — uses the envelope's
 *     `unfiltered_total` to surface scope honestly.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, X } from 'lucide-react';

const FILTER_DEBOUNCE_MS = 350;

const STATUS_CHIPS = [
  { value: 'draft',     label: 'Draft' },
  { value: 'ready',     label: 'Ready' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'archived',  label: 'Archived' },
];

const SORT_OPTIONS = [
  { value: 'newest',       label: 'Newest first' },
  { value: 'oldest',       label: 'Oldest first' },
  { value: 'updated_desc', label: 'Recently updated' },
  { value: 'status_asc',   label: 'Status (Draft → Archived)' },
  { value: 'fit_desc',     label: 'Best fit first' },
  { value: 'fit_asc',      label: 'Worst fit first' },
];

export default function PackListFilters({
  qFromUrl, statusesFromUrl, sortFromUrl,
  total, unfilteredTotal,
  onQChange, onStatusToggle, onSortChange, onClearFilters,
}) {
  const [qInput, setQInput] = useState(qFromUrl || '');

  // Resync local input when URL changes externally (back button,
  // clear filters, share link).
  useEffect(() => {
    setQInput((v) => (v === (qFromUrl || '') ? v : (qFromUrl || '')));
  }, [qFromUrl]);

  // Debounce input → URL.
  useEffect(() => {
    const t = setTimeout(() => {
      const v = qInput.trim();
      if (v !== (qFromUrl || '')) onQChange(v);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qInput, qFromUrl, onQChange]);

  const filtersActive = (qFromUrl && qFromUrl.length > 0)
    || statusesFromUrl.length > 0
    || (sortFromUrl && sortFromUrl !== 'newest');

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="packs-filters"
    >
      {/* Search + sort row */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ndor-ink-muted pointer-events-none" />
          <input
            type="text"
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search pack name…"
            className="h-9 w-full rounded-md border border-ndor-edge bg-ndor-surface pl-8 pr-3 text-sm placeholder:text-ndor-ink-muted focus:outline-none focus:ring-1 focus:ring-ndor-info"
            data-testid="packs-filter-q-input"
            aria-label="Search packs by name"
          />
        </div>
        <Select
          value={sortFromUrl || 'newest'}
          onValueChange={onSortChange}
        >
          <SelectTrigger
            className="h-9 w-[200px] text-sm"
            data-testid="packs-filter-sort-trigger"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORT_OPTIONS.map((o) => (
              <SelectItem
                key={o.value}
                value={o.value}
                data-testid={`packs-filter-sort-option-${o.value}`}
              >
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filtersActive && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-ndor-ink-muted"
            data-testid="packs-filter-clear-btn"
          >
            <X className="h-3 w-3 mr-1" />
            Clear filters
          </Button>
        )}
      </div>

      {/* Status chips row */}
      <div
        className="flex flex-wrap items-center gap-1.5"
        data-testid="packs-filter-status-chips"
      >
        <span className="text-[11px] uppercase tracking-[0.16em] text-ndor-ink-muted font-mono-ui mr-1">
          Status
        </span>
        {STATUS_CHIPS.map((chip) => {
          const active = statusesFromUrl.includes(chip.value);
          return (
            <Badge
              key={chip.value}
              variant="outline"
              role="button"
              tabIndex={0}
              onClick={() => onStatusToggle(chip.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onStatusToggle(chip.value);
                }
              }}
              className={`cursor-pointer text-[11px] select-none ${
                active
                  ? 'bg-ndor-info-light text-ndor-info border-ndor-info/40'
                  : 'bg-ndor-surface-alt text-ndor-ink-muted border-ndor-edge hover:border-ndor-ink-muted'
              }`}
              data-testid={`packs-filter-status-${chip.value}`}
              data-active={active ? '1' : '0'}
            >
              {chip.label}
            </Badge>
          );
        })}
      </div>

      {/* Counter — only visible when filters are active or pagination
          has narrowed the slice, so it doesn't add noise on the
          unfiltered first-page case. */}
      {filtersActive && unfilteredTotal != null && (
        <p
          className="text-xs text-ndor-ink-muted"
          data-testid="packs-filter-counter"
        >
          Showing <span className="tabular-nums font-medium text-ndor-ink">{total.toLocaleString()}</span>
          {' '}of <span className="tabular-nums">{unfilteredTotal.toLocaleString()}</span> pack{unfilteredTotal === 1 ? '' : 's'}
        </p>
      )}
    </div>
  );
}

PackListFilters.propTypes = {
  qFromUrl:        PropTypes.string,
  statusesFromUrl: PropTypes.arrayOf(PropTypes.string).isRequired,
  sortFromUrl:     PropTypes.string,
  total:           PropTypes.number.isRequired,
  unfilteredTotal: PropTypes.number,
  onQChange:       PropTypes.func.isRequired,
  onStatusToggle:  PropTypes.func.isRequired,
  onSortChange:    PropTypes.func.isRequired,
  onClearFilters:  PropTypes.func.isRequired,
};

PackListFilters.defaultProps = {
  qFromUrl: '',
  sortFromUrl: 'newest',
  unfilteredTotal: null,
};
